import type * as PIXI from 'pixi.js';

interface CacheData {
    texture: PIXI.Texture;
    lastAccessed: number;
}

export class GlobalMediaTextureCache {
    cacheData: Map<string, CacheData>;
    MAX_CACHE_SIZE: number;
    CACHE_CLEAR_SIZE: number;

    constructor() {
        this.cacheData = new Map();
        this.MAX_CACHE_SIZE = 200;
        this.CACHE_CLEAR_SIZE = 160;
    }

    getCacheKey(url: string, time: number) {
        return `${url}|${time}`;
    }

    set(url: string, time: number, texture: PIXI.Texture) {
        const cacheKey = this.getCacheKey(url, time);
        if (this.cacheData.has(cacheKey)) {
            this.#destroyEntry(cacheKey);
        }
        this.cacheData.set(cacheKey, { texture, lastAccessed: window.performance.now() });
        this.#capSize();
    }

    get(url: string, time: number) {
        const cacheKey = this.getCacheKey(url, time);
        const cacheData = this.cacheData.get(cacheKey);
        if (!cacheData) {
            return;
        }
        const cachedTexture = cacheData.texture;
        if (!cachedTexture.valid) {
            this.cacheData.delete(cacheKey);
            return;
        }
        this.#recordAccess(cacheKey);
        return cachedTexture;
    }

    get cacheSize() {
        return this.cacheData.size;
    }

    destroy() {
        for (const cacheKey of this.cacheData.keys()) {
            this.#destroyEntry(cacheKey);
        }
    }

    #recordAccess(cacheKey: string) {
        const cacheData = this.cacheData.get(cacheKey);
        if (!cacheData) {
            throw new Error('cache entry does not exist');
        }
        cacheData.lastAccessed = window.performance.now();
    }

    #capSize() {
        if (this.cacheSize <= this.MAX_CACHE_SIZE) {
            return;
        }

        // make a list of all the urls, plus when they were last accessed
        let accessData: { cacheKey: string; lastAccessed: number; }[] = [];
        for (const [cacheKey, cacheData] of this.cacheData.entries()) {
            accessData.push({ cacheKey, lastAccessed: cacheData.lastAccessed });
        }

        // sort the list by the lastAccessed time
        accessData = accessData.sort((one, two) => {
            if (one.lastAccessed < two.lastAccessed) {
                return -1;
            }
            else if (one.lastAccessed > two.lastAccessed) {
                return 1;
            }
            return 0;
        });

        let index = 0;
        // remove entries until we hit the proper cache size
        while (this.cacheSize > this.CACHE_CLEAR_SIZE) {
            const { cacheKey } = accessData[index];
            this.#destroyEntry(cacheKey);
            index += 1;
        }
    }

    #destroyEntry(cacheKey: string) {
        const cacheData = this.cacheData.get(cacheKey);
        if (!cacheData) {
            console.warn('trying to delete a non-existent cache entry: ', cacheKey);
            return;
        }
        if (!cacheData.texture.baseTexture.destroyed) {
            cacheData.texture.destroy(true);
        }
        this.cacheData.delete(cacheKey);
    }
}
